import { Axis } from '../../spatial-classes';
import { BlueprintInstanceType } from '../shared.enums';
import { Dimension } from './dimension.class';
import { dimensionsData } from './dimensions.data';
import { Publish } from '../../decorators';
import { DimensionsData, DimensionsDataIn, DimensionsDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export class Dimensions extends KitchenDesignBase<DimensionsDataIn> {
	public children: Array<Dimension>;
	readonly frontPanelId: string;
	private data: DimensionsData;
	/**
	 * The constructor of the 'Dimensions' class
	 * @param options provides context, also used to hydrate the instance
	 */
	constructor (options: KitchenDesignOptions<DimensionsDataIn> = {}) {
		super(BlueprintInstanceType.Dimensions, options);

		const { dataIn = null } = options;
		const data = dimensionsData(dataIn)
		this.children = [];

		if (data?.children) {
			// Create Dimension instances using the existing data
			data.children
				.forEach((dimensionData) => {
					this.children.push(new Dimension({ dataIn: dimensionData, parent: this }));
				});
		}

		this.setInitialized();
	}

	/**
	 * removes dimensions from the dimensions instance where either the source or target id matches the provided id.
	 * also removes view-suffixed variants (e.g. sourceId 'abc:ct' matches when deleting by id 'abc')
	 * @param id id of the dimension source/target to be removed
	 */
	public delete (id: string): void {
		this.children = this.children.filter((dim: Dimension) => {
			const match = dim.sourceId === id || dim.targetId === id
				|| dim.sourceId?.startsWith(`${id}:`) || dim.targetId?.startsWith(`${id}:`);
			return !match;
		});
	}

	public newDimension (sourceId: string | null, targetId: string | null, axis: Axis): Dimension | null {
		// both refs cannot be null
		if (sourceId === null && targetId === null) return null;

		const dim = new Dimension({ dataIn: { sourceId, targetId, axis: axis.serialize() }, parent: this });
		this.children.push(dim);
		return dim;
	}

	/**
	 * find a dimension by source and target ids
	 * @param refs
	 * @param createIfNotFound
	 * @returns Dimension | null
	 */
	// eslint-disable-next-line max-params, max-len
	public find (refs: [string | null, string | null, string | null], axis: Axis, createIfNotFound = false, options: {shouldHide?: boolean, lineFactor?: number | null, labelFactor?: number | null} = {}): Dimension | null {
		let record1: Dimension | null = null;
		let record: Dimension | null = null;
		// both refs cannot be null
		if (refs[0] === null && refs[1] === null) {
			return null;
		}

		// look for a record
		record = this.children.find((dim: Dimension) => {
			// set any null values in either data set to 0
			// if (dim.axis.x === null) dim.axis.x = 0;
			// if (dim.axis.y === null) dim.axis.y = 0;
			// if (dim.axis.z === null) dim.axis.z = 0;
			// if (axis.x === null) axis.x = 0;
			// if (axis.y === null) axis.y = 0;
			// if (axis.z === null) axis.z = 0;

			return (dim.sourceId === refs[0]) &&
				(dim.targetId === refs[1]) &&
				(dim.axis.x === axis.x) &&
				(dim.axis.y === axis.y) &&
				(dim.axis.z === axis.z);
		});

		if (!record && createIfNotFound) {
			record1 = this.newDimension(refs[0], refs[1], axis);
			if (options.lineFactor !== null && !isNaN(options.lineFactor)) record1.lineFactor = options.lineFactor;
			if (options.labelFactor !== null && !isNaN(options.labelFactor)) record1.labelFactor = options.labelFactor;
			if (options.shouldHide) record1.autoVisible = false;
		}

		if (record && typeof options.shouldHide === 'boolean') record.autoVisible = !options.shouldHide;
		return record ? record : record1 ?? null;
	}

	/**
	 * Shows all hidden dimensions
	 */
	@Publish('dimensions', 'show_all')
	public showAllDimensions (): void {
		this.children.forEach((dimension) => {
			if (!dimension.isVisible) {
				dimension.isVisible = true;
			}
		});
	}

	@Publish('dimensions', 'purge')
	public purge (): void {
		const currentCount = this.children.length;
		this.children = [];
		console.debug('dimensions:purge:count', currentCount);
	}

	/**
	 *
	 * @returns serialized data representing class state for persistence
	 */
	// eslint-disable-next-line class-methods-use-this
	public serialize (): DimensionsDataOut {
		return {
			// children: []
			children: this.children
				.filter((panel: Dimension) => !(panel.sourceId === null && panel.targetId === null))
				.map((panel: Dimension) => panel.serialize())
		};
	}
}
